/* eslint-disable react-hooks/exhaustive-deps */
// Parliament3DView.js
import React, { useRef, useEffect, useState } from "react";
import * as THREE from "three";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls";
import "../styles/Parliament3DView.css";

export default function Parliament3DView({
  mps,
  partyData,
  selectedMp,
  setSelectedMp,
  highlightParty,
  highlightVote,
  activeFilter,
}) {
  const mountRef = useRef(null);
  const scene = useRef(null);
  const camera = useRef(null);
  const renderer = useRef(null);
  const controls = useRef(null);
  const mpMeshes = useRef({});
  const [hoveredMp, setHoveredMp] = useState(null);

  // Debug helper function
  const debug = (message, data) => {
    console.log(`[Parliament3D] ${message}`, data || "");
  };

  
  const getFilteredMpIds = () => {
    // If activeFilter is "none", return all MP IDs to show everything
    if (!activeFilter || activeFilter === "none") {
      console.log("[Parliament3D] No filtering - showing all MPs");
      return new Set(mps.map(mp => mp.id));
    }
  
    // Otherwise apply filtering
    let filteredMps = [];
    
    if (activeFilter === "aye") {
      filteredMps = mps.filter((mp) => mp.voteStatus === "aye");
      console.log(`[Parliament3D] Filtering by vote "aye": ${filteredMps.length} MPs`);
    } 
    else if (activeFilter === "no") {
      filteredMps = mps.filter((mp) => mp.voteStatus === "no");
      console.log(`[Parliament3D] Filtering by vote "no": ${filteredMps.length} MPs`);
    } 
    else if (activeFilter === "unknown") {
      filteredMps = mps.filter((mp) => mp.voteStatus === "unknown");
      console.log(`[Parliament3D] Filtering by vote "unknown": ${filteredMps.length} MPs`);
    } 
    else if (Object.keys(partyData).includes(activeFilter)) {
      filteredMps = mps.filter((mp) => mp.party === activeFilter);
      console.log(`[Parliament3D] Filtering by party "${activeFilter}": ${filteredMps.length} MPs`);
    } 
    else {
      // If filter value doesn't match any condition, show all MPs
      filteredMps = [...mps];
      console.log(`[Parliament3D] Unknown filter "${activeFilter}" - showing all MPs`);
    }
  
    return new Set(filteredMps.map((mp) => mp.id));
  };
  
 

  // Create mock data if needed
  const createMockData = () => {
    if (mps && mps.length > 0) return mps; // Only create mock data if mps is empty

    debug("Creating mock data for testing");

    // Mock party data if not provided
    // const mockPartyData = {
    //   Liberal: { color: '#0047AB', name: 'Liberal Party' },
    //   Labor: { color: '#E81B23', name: 'Labor Party' },
    //   Green: { color: '#00A651', name: 'Green Party' },
    //   Independent: { color: '#808080', name: 'Independent' }
    // };

    // Create mock MPs
    const mockMps = [];
    const parties = ["Liberal", "Labor", "Green", "Independent"];
    const voteStatuses = ["support", "oppose", "unknown"];

    // Create 120 mock MPs
    for (let i = 0; i < 120; i++) {
      const party = parties[i % 4]; // Cycle through parties
      const voteStatus = voteStatuses[Math.floor(i / 40)]; // Groups of 40 with same vote

      mockMps.push({
        id: `mp-${i}`,
        name: `MP ${i + 1}`,
        party: party,
        voteStatus: voteStatus,
        constituency: `Constituency ${i + 1}`,
      });
    }

    return mockMps;
  };

  // Initialize ThreeJS scene
  useEffect(() => {
    if (!mountRef.current) return;

    debug("Initializing Three.js scene");

    // Clear any existing content
    while (mountRef.current.firstChild) {
      mountRef.current.removeChild(mountRef.current.firstChild);
    }

    // Create scene
    scene.current = new THREE.Scene();
    scene.current.background = new THREE.Color(0xf0f8ff);

    // Setup camera
    const width = mountRef.current.clientWidth;
    const height = mountRef.current.clientHeight;
    camera.current = new THREE.PerspectiveCamera(60, width / height, 0.1, 1000);
    camera.current.position.set(0, 10, 15); // Better initial position

    // Setup renderer with optimized settings
    renderer.current = new THREE.WebGLRenderer({
      antialias: true,
      powerPreference: "high-performance",
      precision: "mediump",
    });
    renderer.current.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.current.setSize(width, height);
    renderer.current.shadowMap.enabled = true;
    renderer.current.shadowMap.type = THREE.PCFShadowMap;
    mountRef.current.appendChild(renderer.current.domElement);

    // Add controls
    controls.current = new OrbitControls(
      camera.current,
      renderer.current.domElement
    );
    controls.current.enableDamping = true;
    controls.current.dampingFactor = 0.05;
    controls.current.minDistance = 5;
    controls.current.maxDistance = 30;

    // Make sure camera is looking at the center
    camera.current.lookAt(0, 0, 0);

    // Add lighting
    const ambientLight = new THREE.AmbientLight(0xffffff, 0.7); // Brighter ambient light
    scene.current.add(ambientLight);

    const directionalLight = new THREE.DirectionalLight(0xffffff, 1.0); // Brighter directional light
    directionalLight.position.set(5, 15, 5);
    directionalLight.castShadow = true;
    directionalLight.shadow.mapSize.width = 512;
    directionalLight.shadow.mapSize.height = 512;
    scene.current.add(directionalLight);

    // Add a secondary directional light from another angle
    const secondLight = new THREE.DirectionalLight(0xffffff, 0.5);
    secondLight.position.set(-5, 10, -5);
    scene.current.add(secondLight);

    // Create chamber floor
    const floorGeometry = new THREE.CircleGeometry(12, 32);
    const floorMaterial = new THREE.MeshStandardMaterial({
      color: 0x1a3c5e,
      metalness: 0.1,
      roughness: 0.8,
    });
    const floor = new THREE.Mesh(floorGeometry, floorMaterial);
    floor.rotation.x = -Math.PI / 2;
    floor.receiveShadow = true;
    floor.position.y = 0; // Ensure floor is at y=0
    scene.current.add(floor);

    // Add center table (speaker's position)
    const tableGeometry = new THREE.BoxGeometry(3, 0.5, 1.5);
    const tableMaterial = new THREE.MeshStandardMaterial({
      color: 0x5d4037,
      metalness: 0.3,
      roughness: 0.8,
    });
    const table = new THREE.Mesh(tableGeometry, tableMaterial);
    table.position.set(0, 0.25, 1);
    table.castShadow = true;
    table.receiveShadow = true;
    scene.current.add(table);

    // Add government Leader's chair
    const govLeaderGeometry = new THREE.BoxGeometry(1, 1.2, 1);
    const govLeaderMaterial = new THREE.MeshStandardMaterial({
      color: 0x3e2723,
    });
    const govLeader = new THREE.Mesh(govLeaderGeometry, govLeaderMaterial);
    govLeader.position.set(0, 0.6, 2.5);
    govLeader.castShadow = true;
    govLeader.receiveShadow = true;
    scene.current.add(govLeader);

    // Add Opposite Leader's chair
    const oppLeaderGeometry = new THREE.BoxGeometry(1, 1.2, 1);
    const oppLeaderMaterial = new THREE.MeshStandardMaterial({
      color: 0x3e2723,
    });
    const oppLeader = new THREE.Mesh(oppLeaderGeometry, oppLeaderMaterial);
    oppLeader.position.set(0, 0.6, -0.5);
    oppLeader.castShadow = true;
    oppLeader.receiveShadow = true;
    scene.current.add(oppLeader);

    // Add a nameplate for the Speaker
    const hansardMaterial = new THREE.MeshStandardMaterial({ color: 0xded7b9 });
    const hansardGeometry = new THREE.BoxGeometry(2, 0.1, 0.5);
    const hansard = new THREE.Mesh(hansardGeometry, hansardMaterial);
    hansard.position.set(0, 0.6, 1);
    hansard.castShadow = true;
    hansard.receiveShadow = true;
    scene.current.add(hansard);

    // Add government Leader's chair
    const speakerGeometry = new THREE.BoxGeometry(1, 2.5, 2);
    const speakerMaterial = new THREE.MeshStandardMaterial({ color: 0x3e2723 });
    const speaker = new THREE.Mesh(speakerGeometry, speakerMaterial);
    speaker.position.set(-5, 0.4, 1);
    speaker.castShadow = true;
    speaker.receiveShadow = true;
    scene.current.add(speaker);

    // // Add visual axes for debugging
    // const axesHelper = new THREE.AxesHelper(5);
    // scene.current.add(axesHelper);

    // // Add a grid for reference
    // const gridHelper = new THREE.GridHelper(20, 20);
    // scene.current.add(gridHelper);

    // Setup mouse handlers with throttling
    const raycaster = new THREE.Raycaster();
    const mouse = new THREE.Vector2();

    let throttledMove = false;
    const handleMouseMove = (event) => {
      if (throttledMove) return;
      throttledMove = true;

      setTimeout(() => {
        throttledMove = false;
        if (!mountRef.current || !camera.current) return;

        const rect = mountRef.current.getBoundingClientRect();
        mouse.x =
          ((event.clientX - rect.left) / mountRef.current.clientWidth) * 2 - 1;
        mouse.y =
          -((event.clientY - rect.top) / mountRef.current.clientHeight) * 2 + 1;

        raycaster.setFromCamera(mouse, camera.current);

        const mpObjects = Object.values(mpMeshes.current);
        const intersects = raycaster.intersectObjects(mpObjects);

        if (intersects.length > 0) {
          const hoveredMpId = intersects[0].object.userData.mpId;
          const mp = mps.find((mp) => mp.id === hoveredMpId);
          setHoveredMp(mp);
          document.body.style.cursor = "pointer";
        } else {
          setHoveredMp(null);
          document.body.style.cursor = "default";
        }
      }, 50);
    };

    const handleMouseClick = (event) => {
      if (!mountRef.current || !camera.current) return;

      const rect = mountRef.current.getBoundingClientRect();
      mouse.x =
        ((event.clientX - rect.left) / mountRef.current.clientWidth) * 2 - 1;
      mouse.y =
        -((event.clientY - rect.top) / mountRef.current.clientHeight) * 2 + 1;

      raycaster.setFromCamera(mouse, camera.current);

      const mpObjects = Object.values(mpMeshes.current);
      const intersects = raycaster.intersectObjects(mpObjects);

      if (intersects.length > 0) {
        const selectedMpId = intersects[0].object.userData.mpId;
        const mp = mps.find((mp) => mp.id === selectedMpId);
        setSelectedMp(mp === selectedMp ? null : mp);
      }
    };

    mountRef.current.addEventListener("click", handleMouseClick);
    mountRef.current.addEventListener("mousemove", handleMouseMove);

    // Handle window resize with throttling
    let resizeTimeout;
    const handleResize = () => {
      if (resizeTimeout) clearTimeout(resizeTimeout);

      resizeTimeout = setTimeout(() => {
        if (!mountRef.current || !renderer.current || !camera.current) return;

        const width = mountRef.current.clientWidth;
        const height = mountRef.current.clientHeight;

        camera.current.aspect = width / height;
        camera.current.updateProjectionMatrix();

        renderer.current.setSize(width, height);
      }, 100);
    };

    window.addEventListener("resize", handleResize);

    // Animation loop with frame rate limiting
    let lastTime = 0;
    const frameRateLimit = 30;
    const frameTime = 1000 / frameRateLimit;
    let animationFrameId;

    const animate = (time) => {
      animationFrameId = requestAnimationFrame(animate);

      // Limit frame rate
      const delta = time - lastTime;
      if (delta < frameTime) return;
      lastTime = time - (delta % frameTime);

      if (controls.current) controls.current.update();

      if (renderer.current && scene.current && camera.current) {
        renderer.current.render(scene.current, camera.current);
      }
    };

    animate(0);

    debug("Three.js scene initialized - optimized version");

    return () => {
      debug("Cleaning up Three.js resources");
      window.removeEventListener("resize", handleResize);
      mountRef.current?.removeEventListener("click", handleMouseClick);
      mountRef.current?.removeEventListener("mousemove", handleMouseMove);

      if (animationFrameId) {
        cancelAnimationFrame(animationFrameId);
      }

      if (renderer.current) {
        if (
          mountRef.current &&
          mountRef.current.contains(renderer.current.domElement)
        ) {
          // eslint-disable-next-line react-hooks/exhaustive-deps
          mountRef.current.removeChild(renderer.current.domElement);
        }
        renderer.current.dispose();
      }

      // Dispose of geometries and materials
      Object.values(mpMeshes.current).forEach((mesh) => {
        scene.current.remove(mesh);
        if (mesh.geometry) mesh.geometry.dispose();
        if (mesh.material) {
          if (Array.isArray(mesh.material)) {
            mesh.material.forEach((mat) => mat.dispose());
          } else {
            mesh.material.dispose();
          }
        }
      });

      if (scene.current) {
        scene.current.clear();
      }

      document.body.style.cursor = "default";
    };
  }, [mps, selectedMp, setSelectedMp]);

  // Render MPs

 useEffect(() => {
    if (!scene.current) {
      debug("Scene not initialized");
      return;
    }

    // Use real data or mock data
    const mpData = mps && mps.length > 0 ? mps : createMockData();
    debug(`Rendering ${mpData.length} MPs`);

    if (!mpData || mpData.length === 0) {
      debug("No MP data available");
      return;
    }

    // Create shared geometry for all MPs
    const mpSphereGeometry = new THREE.SphereGeometry(0.3, 12, 12);
    
    // Get filtered MP IDs
    const filteredMpIds = getFilteredMpIds();
    
    // Log filter status
    debug(`Filter status: activeFilter="${activeFilter}", filtered MPs: ${filteredMpIds.size}/${mpData.length}`);
    
    // IMPORTANT: Find and remove all previous MP containers
    const containersToRemove = [];
    scene.current.traverse((object) => {
      // Look for group objects that might contain MPs
      if (object instanceof THREE.Group && object.userData.isMpContainer) {
        containersToRemove.push(object);
      }
    });
    
    // Remove all old containers
    containersToRemove.forEach(container => {
      debug(`Removing old MP container with ${container.children.length} MPs`);
      scene.current.remove(container);
    });
    
    // Clear existing MP meshes reference
    Object.values(mpMeshes.current).forEach((mesh) => {
      // Dispose of geometries and materials to prevent memory leaks
      if (mesh.geometry) mesh.geometry.dispose();
      if (mesh.material) {
        if (Array.isArray(mesh.material)) {
          mesh.material.forEach(mat => mat.dispose());
        } else {
          mesh.material.dispose();
        }
      }
    });
    mpMeshes.current = {};

    const baseMaterials = {
      // Liberal - Royal blue (brighter and more saturated)
      Liberal: new THREE.MeshStandardMaterial({
        color: new THREE.Color(partyData?.Liberal?.color || "#0052CC"),
        emissive: 0x0033AA,
        emissiveIntensity: 0.2,
        roughness: 0.6,
        metalness: 0.2,
      }),
      
      // Labor - Bright red (more vibrant)
      Labor: new THREE.MeshStandardMaterial({
        color: new THREE.Color(partyData?.Labor?.color || "#FF0033"),
        emissive: 0xDD0000,
        emissiveIntensity: 0.2,
        roughness: 0.6,
        metalness: 0.2,
      }),
      
      // Greens - Bright green (more vibrant)
      Greens: new THREE.MeshStandardMaterial({
        color: new THREE.Color(partyData?.Greens?.color || "#00CC44"),
        emissive: 0x00AA33,
        emissiveIntensity: 0.2,
        roughness: 0.6,
        metalness: 0.2,
      }),
      
      // Independent - Silver-grey (more distinct)
      Independent: new THREE.MeshStandardMaterial({
        color: new THREE.Color(partyData?.Independent?.color || "#A0A0A0"),
        emissive: 0x808080,
        emissiveIntensity: 0.1,
        roughness: 0.6,
        metalness: 0.4,
      }),
      
      // LNP - Navy blue (darker than Liberal)
      LNP: new THREE.MeshStandardMaterial({
        color: new THREE.Color(partyData?.LNP?.color || "#003399"),
        emissive: 0x002277,
        emissiveIntensity: 0.2,
        roughness: 0.6,
        metalness: 0.2,
      }),
      
      // National - Green-yellow (traditional National party color)
      National: new THREE.MeshStandardMaterial({
        color: new THREE.Color(partyData?.National?.color || "#FFCC00"),
        emissive: 0xDDAA00,
        emissiveIntensity: 0.2,
        roughness: 0.6,
        metalness: 0.2,
      }),
      
      // CWM - Orange
      CWM: new THREE.MeshStandardMaterial({
        color: new THREE.Color(partyData?.CWM?.color || "#FF6600"),
        emissive: 0xEE4400,
        emissiveIntensity: 0.2,
        roughness: 0.6,
        metalness: 0.2,
      }),
      
      // CA - Teal
      CA: new THREE.MeshStandardMaterial({
        color: new THREE.Color(partyData?.CA?.color || "#00AAAA"),
        emissive: 0x008888,
        emissiveIntensity: 0.2,
        roughness: 0.6,
        metalness: 0.2,
      }),
    };
    
    // Add any additional parties from partyData
    Object.keys(partyData).forEach(party => {
      if (!baseMaterials[party]) {
        baseMaterials[party] = new THREE.MeshStandardMaterial({
          color: new THREE.Color(partyData[party].color || "#CC44CC"), // Purple default for other parties
          emissive: 0xAA33AA,
          emissiveIntensity: 0.2,
          roughness: 0.6,
          metalness: 0.2,
        });
      }
    });

    // Create MP container for batch processing
    const mpContainer = new THREE.Group();
    // Mark this container so we can find it later
    mpContainer.userData.isMpContainer = true;

    // Add MP seats in curved rows
    mpData.forEach((mp, index) => {
      try {
        const party = mp.party || "Independent";
        const voteStatus = mp.voteStatus || "unknown";

        // Get base material for this party, or use Independent as fallback
        const baseMaterial = baseMaterials[party] || baseMaterials["Independent"];
        
        // Clone the base material for this MP
        const material = baseMaterial.clone();

        // Adjust emissive based on vote status
        if (voteStatus === "aye") {
          material.emissive.set(0x70ad47);
          material.emissiveIntensity = 0.3;
        } else if (voteStatus === "no") {
          material.emissive.set(0xed7d31);
          material.emissiveIntensity = 0.3;
        } else {
          material.emissive.set(0xffc000);
          material.emissiveIntensity = 0.2;
        }

        // CRITICAL: Apply transparency if we have an active filter AND this MP is not in the filter set
        if (activeFilter && activeFilter !== "none" && !filteredMpIds.has(mp.id)) {
          material.transparent = true;
          material.opacity = 0.3;
        } else {
          // Make sure it's fully opaque otherwise
          material.transparent = false;
          material.opacity = 1.0;
        }

        // Create sphere representing the MP
        const mpMesh = new THREE.Mesh(mpSphereGeometry, material);
        mpMesh.userData.mpId = mp.id;
        mpMesh.castShadow = true;
        mpMesh.receiveShadow = true;

        // Position MPs in semi-circular rows based on party
        let row, angle, radius;

        switch (party) {
          case "Liberal":
            row = Math.floor((index % 68) / 17);
            radius = 7 + row * 1.2;
            angle = Math.PI * 0.6 * ((index % 17) / 16) - Math.PI * 0.3;
            mpMesh.position.set(
              radius * Math.sin(angle),
              0.3,
              radius * Math.cos(angle) + 1
            );
            break;
            
          case "Labor":
            row = Math.floor((index % 68) / 17);
            radius = 7 + row * 1.2;
            angle = Math.PI * 0.6 * ((index % 17) / 16) + Math.PI * 0.7;
            mpMesh.position.set(
              radius * Math.sin(angle),
              0.3,
              radius * Math.cos(angle) + 1
            );
            break;
            case "LNP":
              row = Math.floor((index % 68) / 17);
              radius = 7 + row * 1.2;
              angle = Math.PI * 0.6 * ((index % 17) / 16) - Math.PI * 0.3;
              mpMesh.position.set(
                radius * Math.sin(angle),
                0.3,
                radius * Math.cos(angle) + 1
              );
              break;
            
          case "Greens":
            radius = 7;
            angle = Math.PI * 0.2 * ((index % 20) / 19) + Math.PI * 0.4;
            mpMesh.position.set(
              radius * Math.sin(angle),
              0.3,
              radius * Math.cos(angle) + 1
            );
            break;
            
          case "Independent":
            radius = 8.2;
            angle = Math.PI * 0.2 * ((index % 16) / 15) + Math.PI * 0.4;
            mpMesh.position.set(
              radius * Math.sin(angle),
              0.3,
              radius * Math.cos(angle) + 1
            );
            break;
            
          // case "LNP":
          //   row = Math.floor((index % 28) / 7);
          //   radius = 9.5 + row * 1.2;
          //   angle = Math.PI * 0.6 * ((index % 7) / 6) - Math.PI * 0.3;
          //   mpMesh.position.set(
          //     radius * Math.sin(angle),
          //     0.3,
          //     radius * Math.cos(angle) + 1
          //   );
          //   break;
            
          case "National":
              row = Math.floor((index % 68) / 17);
              radius = 7 + row * 1.2;
              angle = Math.PI * 0.6 * ((index % 17) / 16) - Math.PI * 0.3;
              mpMesh.position.set(
                radius * Math.sin(angle),
                0.3,
                radius * Math.cos(angle) + 1
              );
              break;
            
          case "CWM":
            radius = 9;
            angle = Math.PI * 0.1 * ((index % 5) / 4) + Math.PI * 0.4;
            mpMesh.position.set(
              radius * Math.sin(angle),
              0.3,
              radius * Math.cos(angle) + 1
            );
            break;
            
          case "CA":
            radius = 9;
            angle = Math.PI * 0.1 * ((index % 5) / 4) + Math.PI * 0.5;
            mpMesh.position.set(
              radius * Math.sin(angle),
              0.3,
              radius * Math.cos(angle) + 1
            );
            break;
            
          default:
            // Default positioning for any other party
            const randomAngle = Math.random() * Math.PI * 2;
            const randomRadius = 7 + Math.random() * 2;
            mpMesh.position.set(
              randomRadius * Math.sin(randomAngle),
              0.3,
              randomRadius * Math.cos(angle) + 1
            );
        }

        // Highlight selected MP
        if (selectedMp && mp.id === selectedMp.id) {
          mpMesh.scale.set(1.5, 1.5, 1.5);
          mpMesh.position.y = 0.5;

          // Add indicator ring for selected MP
          const ringGeometry = new THREE.RingGeometry(0.5, 0.55, 16);
          const ringMaterial = new THREE.MeshBasicMaterial({
            color: 0xffffff,
            side: THREE.DoubleSide,
          });
          const ring = new THREE.Mesh(ringGeometry, ringMaterial);
          ring.position.copy(mpMesh.position);
          ring.position.y = 0.05;
          ring.rotation.x = -Math.PI / 2;
          mpContainer.add(ring);

          // Add a cylinder to connect the MP sphere with the ground
          const cylinderGeometry = new THREE.CylinderGeometry(
            0.05,
            0.05,
            mpMesh.position.y,
            6
          );
          const cylinderMaterial = new THREE.MeshBasicMaterial({
            color: 0xcccccc,
          });
          const cylinder = new THREE.Mesh(cylinderGeometry, cylinderMaterial);
          cylinder.position.set(
            mpMesh.position.x,
            mpMesh.position.y / 2,
            mpMesh.position.z
          );
          mpContainer.add(cylinder);
        }

        // Highlight hovered MP
        if (hoveredMp && mp.id === hoveredMp.id) {
          mpMesh.scale.set(1.3, 1.3, 1.3);
          mpMesh.position.y += 0.15;
          material.emissiveIntensity = 0.6;
        }

        // Highlight by party or vote status
        if (highlightParty === party) {
          mpMesh.scale.set(1.2, 1.2, 1.2);
          mpMesh.position.y += 0.1;
          material.emissiveIntensity = 0.5;
        }

        if (highlightVote === voteStatus) {
          mpMesh.scale.set(1.2, 1.2, 1.2);
          mpMesh.position.y += 0.1;
          material.emissiveIntensity = 0.5;
        }

        // Add MP to container
        mpContainer.add(mpMesh);
        mpMeshes.current[mp.id] = mpMesh;
      } catch (err) {
        debug(`Error rendering MP ${mp?.id || index}:`, err.message);
      }
    });

    // Add the new MP container to scene
    scene.current.add(mpContainer);

    debug(`Rendered ${Object.keys(mpMeshes.current).length} MPs in a new container`);

    // Force camera to look at the scene center
    if (camera.current) {
      camera.current.lookAt(0, 0, 0);
      camera.current.updateProjectionMatrix();
    }
  }, [
    mps,
    partyData,
    selectedMp,
    hoveredMp,
    highlightParty,
    highlightVote,
    activeFilter,
    createMockData,
    getFilteredMpIds,
  ]);

  return (
    <div className="parliament-3d-view">
      <div className="three-container" ref={mountRef}></div>

      {/* MP tooltip display for hover */}
      {hoveredMp && (
        <div className="mp-tooltip">
          <strong>{hoveredMp.name}</strong>
          <span
            className="tooltip-party"
            style={{
              backgroundColor: partyData?.[hoveredMp.party]?.color || "#808080",
              color: "#fff",
            }}
          >
            {hoveredMp.party}
          </span>
          <span className="tooltip-vote">
            Vote:{" "}
            {hoveredMp.voteStatus.charAt(0).toUpperCase() +
              hoveredMp.voteStatus.slice(1)}
          </span>
        </div>
      )}

      <div className="view-controls">
        <div className="view-control-note">
          <p>
            Use mouse to rotate and zoom the view. Click on an MP to select.
          </p>
        </div>
        <div className="view-preset-buttons">
          <button
            className="preset-button"
            onClick={() => {
              if (camera.current) {
                camera.current.position.set(0, 10, 10);
                camera.current.lookAt(0, 0, 0);
                controls.current.update();
              }
            }}
          >
            Top View
          </button>
          <button
            className="preset-button"
            onClick={() => {
              if (camera.current) {
                camera.current.position.set(0, 3, 15);
                camera.current.lookAt(0, 0, 0);
                controls.current.update();
              }
            }}
          >
            Front View
          </button>
          <button
            className="preset-button"
            onClick={() => {
              if (camera.current) {
                camera.current.position.set(15, 5, 0);
                camera.current.lookAt(0, 0, 0);
                controls.current.update();
              }
            }}
          >
            Side View
          </button>
        </div>
      </div>
    </div>
  );
}
